import { create } from 'zustand';
import { persist } from 'zustand/middleware';

export interface Project {
  id: number;
  name: string;
  description?: string;
  use_case: 'family_law' | 'compliance_dora' | 'compliance_iso_27001' | 'compliance_esg' | 'compliance_soc2' | 'rfp_response' | 'security_questionnaire';
  settings: Record<string, any>;
  created_at: string;
  updated_at?: string;
  document_count: number;
  processed_document_count: number;
}

export interface Document {
  id: number;
  project_id: number;
  filename: string;
  file_type: string;
  file_size: number;
  upload_date: string;
  processed: boolean;
  processing_status: 'pending' | 'processing' | 'completed' | 'failed';
  annotation_count?: number;
  error_message?: string;
}

export interface Annotation {
  id: number;
  document_id: number;
  annotation_type: 'entity' | 'date' | 'amount' | 'quote' | 'event' | 'flag';
  content: string;
  context?: string;
  page_number?: number;
  confidence?: number;
  annotation_metadata?: Record<string, any>;
  created_at: string;
}

export interface Template {
  id: number;
  name: string;
  use_case: string;
  language: string;
  template_type: string;
  variable_count: number;
  created_at: string;
}

interface StoreState {
  // Projects
  projects: Project[];
  currentProject: Project | null;
  setProjects: (projects: Project[]) => void;
  setCurrentProject: (project: Project | null) => void;
  addProject: (project: Project) => void;
  updateProject: (id: number, updates: Partial<Project>) => void;
  removeProject: (id: number) => void;
  
  // Documents
  documents: Document[];
  currentDocument: Document | null;
  setDocuments: (docs: Document[]) => void;
  setCurrentDocument: (doc: Document | null) => void;
  addDocument: (doc: Document) => void;
  updateDocument: (id: number, updates: Partial<Document>) => void;
  removeDocument: (id: number) => void;
  
  // Annotations
  annotations: Annotation[];
  setAnnotations: (annotations: Annotation[]) => void;
  
  // Templates
  templates: Template[];
  setTemplates: (templates: Template[]) => void;
  
  // Settings
  llmProvider: 'ollama' | 'claude';
  setLLMProvider: (provider: 'ollama' | 'claude') => void;
  selectedModel: string;  // Global model selection: "claude" (remote) or "ollama" (local)
  setSelectedModel: (model: string) => void;
  selectedTheme: string;  // Theme ID: "default" or "fallout"
  setSelectedTheme: (theme: string) => void;
  apiUrl: string;
  setApiUrl: (url: string) => void;
  
  // UI State
  isProcessing: boolean;
  setProcessing: (status: boolean) => void;
  isSidebarOpen: boolean;
  toggleSidebar: () => void;
  isChatOpen: boolean;
  toggleChat: () => void;
  searchQuery: string;
  setSearchQuery: (query: string) => void;
  generatedDocument: { docId: number; filename: string } | null;
  setGeneratedDocument: (doc: { docId: number; filename: string } | null) => void;
  
  // Notifications
  notifications: Array<{ id: string; type: 'success' | 'error' | 'info'; message: string }>;
  addNotification: (type: 'success' | 'error' | 'info', message: string) => void;
  removeNotification: (id: string) => void;
  
  // Generated Documents
  generatedDocuments: any[];
  selectedDocIndex: number;
  currentProjectId: string | null;
  setGeneratedDocuments: (docs: any[], projectId: string) => void;
  setCurrentProjectId: (projectId: string | null) => void;
  setSelectedDocIndex: (index: number) => void;
  clearGeneratedDocuments: () => void;
}

export const useStore = create<StoreState>()(
  persist(
    (set) => ({
      // Projects
      projects: [],
      currentProject: null,
      setProjects: (projects) => set({ projects }),
      setCurrentProject: (project) => set({ currentProject: project }),
      addProject: (project) => set((state) => ({ 
        projects: [...state.projects, project] 
      })),
      updateProject: (id, updates) => set((state) => ({
        projects: state.projects.map(p => p.id === id ? { ...p, ...updates } : p),
        currentProject: state.currentProject?.id === id 
          ? { ...state.currentProject, ...updates } 
          : state.currentProject
      })),
      removeProject: (id) => set((state) => ({
        projects: state.projects.filter(p => p.id !== id),
        currentProject: state.currentProject?.id === id ? null : state.currentProject
      })),
      
      // Documents
      documents: [],
      currentDocument: null,
      setDocuments: (docs) => set({ documents: docs }),
      setCurrentDocument: (doc) => set({ currentDocument: doc }),
      addDocument: (doc) => set((state) => ({ 
        documents: [...state.documents, doc] 
      })),
      updateDocument: (id, updates) => set((state) => ({
        documents: state.documents.map(d => d.id === id ? { ...d, ...updates } : d),
        currentDocument: state.currentDocument?.id === id 
          ? { ...state.currentDocument, ...updates } 
          : state.currentDocument
      })),
      removeDocument: (id) => set((state) => ({
        documents: state.documents.filter(d => d.id !== id),
        currentDocument: state.currentDocument?.id === id ? null : state.currentDocument
      })),
      
      // Annotations
      annotations: [],
      setAnnotations: (annotations) => set({ annotations }),
      
      // Templates
      templates: [],
      setTemplates: (templates) => set({ templates }),
      
      // Settings
      llmProvider: 'claude',
      setLLMProvider: (provider) => set({ llmProvider: provider }),
      selectedModel: 'claude',  // Default to remote model
      setSelectedModel: (model) => set({ selectedModel: model }),
      selectedTheme: 'default',  // Default to modern professional theme
      setSelectedTheme: (theme) => set({ selectedTheme: theme }),
      apiUrl: 'http://localhost:8000',
      setApiUrl: (url) => set({ apiUrl: url }),
      
      // UI State
      isProcessing: false,
      setProcessing: (status) => set({ isProcessing: status }),
      isSidebarOpen: true,
      toggleSidebar: () => set((state) => ({ isSidebarOpen: !state.isSidebarOpen })),
      isChatOpen: true,
      toggleChat: () => set((state) => ({ isChatOpen: !state.isChatOpen })),
      searchQuery: '',
      setSearchQuery: (query) => set({ searchQuery: query }),
      generatedDocument: null,
      setGeneratedDocument: (doc) => set({ generatedDocument: doc }),
      
      // Notifications
      notifications: [],
      addNotification: (type, message) => {
        const id = Math.random().toString(36).substring(7);
        set((state) => ({
          notifications: [...state.notifications, { id, type, message }]
        }));
        // Auto-remove after 5 seconds
        setTimeout(() => {
          set((state) => ({
            notifications: state.notifications.filter(n => n.id !== id)
          }));
        }, 5000);
      },
      removeNotification: (id) => set((state) => ({
        notifications: state.notifications.filter(n => n.id !== id)
      })),
      
      // Generated Documents
      generatedDocuments: [],
      selectedDocIndex: 0,
      currentProjectId: null,
      setGeneratedDocuments: (docs, projectId) => {
        set({ 
          generatedDocuments: docs,
          currentProjectId: projectId,
          selectedDocIndex: 0 
        });
      },
      setCurrentProjectId: (projectId: string | null) => {
        set({ currentProjectId: projectId });
      },
      setSelectedDocIndex: (index) => set({ selectedDocIndex: index }),
      clearGeneratedDocuments: () => set({ generatedDocuments: [], selectedDocIndex: 0, currentProjectId: null }),
    }),
    {
      name: 'docintel-storage',
      partialize: (state) => ({
        llmProvider: state.llmProvider,
        selectedModel: state.selectedModel,
        selectedTheme: state.selectedTheme,
        apiUrl: state.apiUrl,
        isSidebarOpen: state.isSidebarOpen,
        isChatOpen: state.isChatOpen,
      }),
    }
  )
);
